/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { EChartsOption } from "echarts";
import { CallbackDataParams, TopLevelFormatterParams } from "echarts/types/dist/shared";
import { ScatterPoint } from "../../models/http/charts/scatter-chart/scatter-point.model";
import { ChartType } from "../../models/ui/charts.model";
import { COLORS } from "../constants";
import { deepClone } from "../utils";
import { getChartTooltip, truncateLabelWithEllipsis } from "./chart-utils";

export const BASE_CHART_OPTIONS: EChartsOption = {
	aria: { enabled: true },
	grid: { containLabel: true },
	tooltip: {
		trigger: "item",
		backgroundColor: COLORS.SMART_NAVY_100,
		borderWidth: 1,
		borderColor: COLORS.GREY_100,
		shadowColor: "rgba(0, 0, 0, 0.25)",
		shadowBlur: 12,
		shadowOffsetX: 0,
		shadowOffsetY: 4,
		borderRadius: 4,
		textStyle: { color: COLORS.WHITE },
		align: "center",
		formatter: (params: TopLevelFormatterParams) => getChartTooltip(params)
	},
	textStyle: { fontFamily: "Noto Sans" }
};

export const DEFAULT_CHART_OPTIONS: Record<ChartType, () => EChartsOption> = {
	line: () =>
		deepClone({
			...BASE_CHART_OPTIONS,
			legend: {
				show: true,
				orient: "horizontal",
				itemGap: 16,
				textStyle: {
					fontSize: 14,
					ellipsis: "...",
					overflow: "truncate"
				}
			},
			xAxis: {
				type: "category",
				axisLabel: {
					lineHeight: 24,
					overflow: "truncate",
					ellipsis: "..."
				},
				splitLine: { show: false },
				axisTick: {
					lineStyle: { color: COLORS.WHITE },
					length: 8,
					alignWithLabel: true
				}
			},
			yAxis: {
				nameTextStyle: {
					color: COLORS.GREY_50,
					verticalAlign: "bottom",
					padding: [0, 0, -24, 0]
				},
				type: "value",
				axisLine: { show: false },
				axisLabel: {
					showMaxLabel: false,
					inside: false,
					align: "right",
					verticalAlign: "bottom",
					padding: [0, 0, 5, 8],
					color: COLORS.GREY_50,
					formatter: label => truncateLabelWithEllipsis(label, 25),
					interval: 1
				},
				axisTick: {
					show: false,
					alignWithLabel: true
				},
				splitLine: { showMaxLine: false }
			},
			series: {
				type: "line",
				// showSymbol: true,
				showAllSymbol: false,
				symbolSize: 8
			}
		}),
	barX: () =>
		deepClone({
			...BASE_CHART_OPTIONS,
			xAxis: {
				show: true,
				axisTick: {
					show: true,
					length: 8,
					alignWithLabel: true,
					lineStyle: { color: COLORS.WHITE }
				},
				axisLabel: {
					lineHeight: 24,
					overflow: "truncate",
					ellipsis: "..."
				}
			},
			yAxis: {
				nameTextStyle: {
					color: COLORS.GREY_50,
					verticalAlign: "bottom",
					padding: [0, 0, -30, 0]
				},
				nameGap: 32,
				show: true,
				axisLine: { show: false },
				axisLabel: {
					inside: false,
					align: "right",
					verticalAlign: "bottom",
					padding: [0, 0, 5, 8],
					color: COLORS.GREY_50,
					showMaxLabel: false,
					formatter: (label: string | number) => truncateLabelWithEllipsis(label, 25)
				},
				axisTick: {
					show: false,
					alignWithLabel: true
				},
				splitLine: { showMaxLine: false }
			},
			legend: {
				show: true,
				orient: "horizontal",
				itemGap: 16,
				textStyle: {
					fontSize: 14,
					ellipsis: "...",
					overflow: "truncate"
				}
			},
			series: {
				type: "bar",
				label: {
					show: false,
					position: "top",
					fontSize: 16,
					fontWeight: 700,
					color: COLORS.WHITE
				},
				barMaxWidth: 100
			}
		}),
	barY: () =>
		deepClone({
			...BASE_CHART_OPTIONS,
			xAxis: {
				show: true,
				nameTextStyle: {
					color: COLORS.GREY_50,
					verticalAlign: "bottom",
					padding: [0, 0, 0, 20]
				},
				axisLine: { show: false },
				axisLabel: {
					align: "left",
					verticalAlign: "bottom",
					padding: [0, 10, -10, 5],
					color: COLORS.GREY_50,
					overflow: "truncate",
					ellipsis: "..."
				},
				axisTick: {
					alignWithLabel: true,
					show: true,
					length: 20,
					lineStyle: { color: "#555555" }
				}
			},
			yAxis: {
				show: true,
				alignWithLabel: true,
				axisLabel: {
					formatter: (label: string | number) => truncateLabelWithEllipsis(label, 25)
				},
				axisTick: { show: false },
				axisLine: { show: false }
			},
			series: {
				type: "bar",
				label: {
					show: false,
					position: [5, -15],
					fontSize: 16,
					fontWeight: 700,
					color: COLORS.WHITE
				},
				barMaxWidth: 100
			}
		}),
	pie: () =>
		deepClone({
			...BASE_CHART_OPTIONS,
			series: {
				type: "pie",
				center: ["50%", "50%"],
				radius: ["15%", "65%"],
				label: {
					show: true,
					formatter: "{bold|{b}} \n {bolder|{d}%}",
					overflow: "truncate",
					ellipsis: "...",
					rich: {
						bold: {
							fontWeight: 400,
							fontSize: 14
						},
						bolder: {
							fontWeight: 700,
							fontSize: 14
						}
					}
				},
				labelLine: {
					show: true,
					length: 15,
					length2: 10,
					smooth: true,
					lineStyle: {
						color: COLORS.WHITE,
						width: 1
					}
				}
			}
		}),
	scatter: () =>
		deepClone({
			...BASE_CHART_OPTIONS,
			xAxis: {
				nameTextStyle: {
					color: COLORS.GREY_50,
					verticalAlign: "bottom",
					padding: [0, 0, 4, -64]
				},
				splitLine: { show: false },
				axisTick: {
					show: false,
					alignWithLabel: true
				},
				axisLabel: {
					showMaxLabel: false,
					showMinLabel: false,
					alignMinLabel: "left",
					verticalAlignMinLabel: "bottom",
					verticalAlign: "bottom",
					padding: [0, 0, 12, 0],
					color: COLORS.GREY_50,
					overflow: "truncate",
					ellipsis: "..."
				},
				axisLine: {
					lineStyle: { color: "#555555" }
				}
			},
			yAxis: {
				nameLocation: "end",
				nameTextStyle: {
					color: COLORS.GREY_50,
					padding: [0, 0, -32, 42]
				},
				splitLine: { show: false },
				axisLabel: {
					showMinLabel: true,
					showMaxLabel: false,
					verticalAlignMinLabel: "bottom",
					align: "left",
					padding: [0, 0, 4, 20],
					color: COLORS.GREY_50,
					formatter: (label: string | number) => truncateLabelWithEllipsis(label, 25)
				},
				axisLine: {
					lineStyle: { color: "#555555" }
				},
				axisTick: {
					show: false,
					alignWithLabel: true
				}
			},
			series: {
				type: "scatter",
				symbolSize: 16,
				label: {
					show: true,
					position: "bottom",
					color: COLORS.WHITE,
					formatter: (params: CallbackDataParams) =>
						truncateLabelWithEllipsis((params.data as ScatterPoint).name, 25)
				}
			}
		}),
	map: () =>
		deepClone({
			...BASE_CHART_OPTIONS,
			title: {
				text: "Carbon Intensity",
				left: 0,
				top: 0,
				textStyle: { fontSize: 14 }
			},
			visualMap: {
				type: "piecewise",
				orient: "horizontal",
				inverse: true,
				itemSymbol: "rect",
				top: 0,
				left: 142,
				itemGap: 0,
				splitNumber: 6,
				min: 0,
				max: 100,
				text: ["High  ", "  Low"],
				textStyle: { color: COLORS.WHITE },
				calculable: true,
				pieces: [
					{ max: 19, color: COLORS.GREEN_150 },
					{ min: 20, max: 39, color: COLORS.GREEN_50 },
					{ min: 40, max: 59, color: COLORS.YELLOW_100 },
					{ min: 60, max: 79, color: COLORS.YELLOW_50 },
					{ min: 80, max: 99, color: COLORS.ORANGE_50 },
					{ min: 100, color: COLORS.ORANGE_150 }
				],
				itemWidth: 35,
				outOfRange: { color: [COLORS.GREEN_150, COLORS.ORANGE_150] }
			},
			series: {
				type: "map",
				map: "world",
				roam: true,
				zoom: 4,
				center: [0, 45],
				showLegendSymbol: false,
				scaleLimit: {
					min: 2,
					max: 6
				},
				label: { show: false },
				emphasis: {
					itemStyle: {
						borderColor: COLORS.WHITE,
						borderWidth: 2,
						areaColor: "none"
					},
					label: { show: false }
				},
				select: {
					itemStyle: {
						borderColor: COLORS.WHITE,
						borderWidth: 2,
						areaColor: COLORS.GREY_100
					},
					label: { show: false }
				},
				itemStyle: { areaColor: COLORS.GREY_100 }
			}
		}),
	mixed: () =>
		deepClone({
			...BASE_CHART_OPTIONS,
			legend: {
				show: true,
				orient: "horizontal",
				itemGap: 16,
				textStyle: {
					fontSize: 14,
					ellipsis: "...",
					overflow: "truncate"
				}
			},
			xAxis: {
				axisTick: {
					lineStyle: { color: COLORS.WHITE },
					length: 8,
					alignWithLabel: true
				},
				axisLabel: {
					overflow: "truncate",
					ellipsis: "..."
				}
			},
			yAxis: [
				{
					type: "value",
					axisLabel: {
						formatter: (label: string | number) => truncateLabelWithEllipsis(label, 25)
					}
				},
				{
					type: "value",
					axisLabel: {
						formatter: (label: string | number) => truncateLabelWithEllipsis(label, 25)
					}
				}
			],
			series: {
				showSymbol: true,
				symbolSize: 8
			}
		})
};
